import React from 'react';
import { motion } from 'framer-motion';
import { 
  Home, 
  Info, 
  GraduationCap, 
  DollarSign, 
  Image, 
  Mail, 
  FileText, 
  Shield, 
  Phone,
  MapPin,
  ChevronRight,
  School,
  BookOpen,
  Award,
  FolderOpen
} from 'lucide-react';
import { Link } from 'react-router-dom';
const Sitemap = () => {
  const sitemapSections = [
    {
      title: "Main Pages",
      icon: <Home size={18} />,
      links: [
        { name: "Home", path: "/", description: "Welcome to St. Xavier's Public School" },
        { name: "About Us", path: "/about-us", description: "Learn about our school's history and mission" },
        { name: "Contact Us", path: "/contact", description: "Get in touch with us" }
      ]
    },
    {
      title: "About Section",
      icon: <Info size={18} />,
      links: [
        { name: "Director's Desk", path: "/director-desk", description: "Message from our Director" },
        { name: "Principal's Desk", path: "/principal-desk", description: "Message from our Principal" },
        { name: "Mandatory Disclosure", path: "/mandatory-disclosure", description: "Mandatory school disclosures" },
        { name: "CBSE Details", path: "/cbse-details", description: "CBSE affiliation and school details" }
      ]
    },
    {
      title: "Academics",
      icon: <BookOpen size={18} />,
      links: [
        { name: "Facilities", path: "/facilities", description: "School infrastructure and amenities" },
        { name: "Fee Structure", path: "/fee-structure", description: "Complete fee details" },
        { name: "Admission", path: "/admission", description: "Admission process and forms" },
        { name: "Student TC", path: "/student-tc", description: "Transfer Certificate" }
      ]
    },
    {
      title: "Resources",
      icon: <FolderOpen size={18} />,
      links: [
        { name: "Gallery", path: "/gallery", description: "School photos and events" },
        { name: "Enquiry", path: "/enquiry", description: "Admission enquiry form" },
        { name: "Transfer Certificate", path: "/transfer-certificate", description: "Get transfer certificate" }
      ]
    },
    {
      title: "Legal",
      icon: <Shield size={18} />,
      links: [
        { name: "Privacy Policy", path: "/privacy-policy", description: "Our privacy practices" },
        { name: "Terms of Use", path: "/terms-of-use", description: "Terms and conditions" },
        { name: "Sitemap", path: "/sitemap", description: "Website sitemap" }
      ]
    }
  ];

  const quickLinks = [
    { name: "Admission Open", path: "/admission", icon: <GraduationCap size={14} /> },
    { name: "Fee Structure", path: "/fee-structure", icon: <DollarSign size={14} /> },
    { name: "Gallery", path: "/gallery", icon: <Image size={14} /> },
    { name: "Contact", path: "/contact", icon: <Phone size={14} /> }
  ];

  const contactInfo = [
    { icon: <MapPin size={14} />, text: "Bela Cholapur Varanasi - 221101, UP" },
    { icon: <Phone size={14} />, text: "+91 8601017877, +91 9450785369" },
    { icon: <Mail size={14} />, text: "st.xavierbelavns@gmail.com" }
  ];
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <motion.div
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="inline-block"
          >
            <span className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-5 py-2 rounded-full text-sm font-semibold shadow-lg">
              <FolderOpen size={16} />
              Website Navigation
            </span>
          </motion.div>
          <h1 className="text-3xl sm:text-4xl font-bold mt-5 bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 bg-clip-text text-transparent">
            Sitemap
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 via-red-500 to-blue-600 mx-auto mt-3 rounded-full"></div>
          <p className="text-gray-500 mt-3">Complete navigation guide for St. Xavier's Public School website</p>
        </motion.div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {sitemapSections.map((section, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}
                className="bg-white rounded-2xl shadow-md overflow-hidden"
              >
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-3">
                  <div className="flex items-center gap-2">
                    <div className="text-white">{section.icon}</div>
                    <h2 className="text-white font-bold text-lg">{section.title}</h2>
                  </div>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {section.links.map((link, linkIdx) => (
                      <Link
                        key={linkIdx}
                        to={link.path}
                        className="group flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-blue-50 transition-all duration-300"
                      >
                        <div>
                          <span className="font-medium text-gray-800 group-hover:text-blue-600 transition">
                            {link.name}
                          </span>
                          <p className="text-xs text-gray-400 mt-0.5">{link.description}</p>
                        </div>
                        <ChevronRight size={16} className="text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                      </Link>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-2xl shadow-md overflow-hidden"
            >
              <div className="px-5 py-3 bg-black/10">
                <h2 className="text-white font-bold text-lg flex items-center gap-2">
                  <Award size={18} />
                  Quick Links
                </h2>
              </div>
              <div className="p-4 space-y-2">
                {quickLinks.map((link, idx) => (
                  <Link
                    key={idx}
                    to={link.path}
                    className="flex items-center gap-3 p-3 bg-white/20 rounded-xl hover:bg-white/30 transition-all duration-300 text-white group"
                  >
                    {link.icon}
                    <span className="font-medium">{link.name}</span>
                    <ChevronRight size={14} className="ml-auto opacity-0 group-hover:opacity-100 transition-all" />
                  </Link>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
              className="bg-white rounded-2xl shadow-md overflow-hidden"
            >
              <div className="bg-gradient-to-r from-gray-700 to-gray-800 px-5 py-3">
                <h2 className="text-white font-bold text-lg flex items-center gap-2">
                  <Phone size={18} />
                  Contact Us
                </h2>
              </div>
              <div className="p-4 space-y-3">
                {contactInfo.map((info, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-gray-600 text-sm">
                    <div className="text-blue-500">{info.icon}</div>
                    <span>{info.text}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.4 }}
              className="bg-gradient-to-r from-green-600 to-teal-600 rounded-2xl shadow-md overflow-hidden"
            >
              <div className="p-5 text-center text-white">
                <School className="w-10 h-10 mx-auto mb-2 opacity-80" />
                <h3 className="text-xl font-bold">St. Xavier's Public School</h3>
                <p className="text-green-100 text-sm mt-1">CBSE Affiliated Senior Secondary School</p>
                <div className="mt-4 pt-3 border-t border-green-400 flex justify-around">
                  <div>
                    <p className="text-2xl font-bold">25+</p>
                    <p className="text-xs opacity-80">Years of Excellence</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold">5000+</p>
                    <p className="text-xs opacity-80">Students</p>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.45 }}
              className="bg-white rounded-2xl shadow-md p-5 text-center"
            >
              <FileText className="w-8 h-8 mx-auto text-blue-500 mb-2" />
              <p className="text-2xl font-bold text-gray-800">15+</p>
              <p className="text-xs text-gray-500">Pages Available</p>
            </motion.div>
          </div>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-10 text-center"
        >
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-sm text-gray-500">
              📍 Bela Cholapur Varanasi - 221101, Uttar Pradesh, India
            </p>
            <p className="text-xs text-gray-400 mt-2">
              © {new Date().getFullYear()} St. Xavier's Public School. All rights reserved.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
export default Sitemap;